'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDebounce } from '@/hooks/use-debounce';

type Entry = {
    ratio: number;
    isPlaybackCandidate: boolean;
    playing: boolean;
    setPlaying: (playing: boolean) => void;
};

type SingleVideoCoordinator = {
    soundEnabled: boolean;
    toggleSound: () => void;
    register: (id: string, setPlaying: (playing: boolean) => void) => () => void;
    report: (id: string, ratio: number, isPlaybackCandidate: boolean) => void;
};

const CoordinatorContext = createContext<SingleVideoCoordinator | null>(null);

const RATIO_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];
const SETTLE_MS = 150;

/**
 * Coordinates video playback and sound across every video rendered on the page. Videos register via
 * `usePageVideoCoordinator` and report their visibility; the provider lets only one video play at a
 * time – the most-visible one eligible to play (`isPlaybackCandidate`) – and shares one page-wide mute
 * state. Without it, each video autoplays on its own whenever it's in view, so multiple videos/zones
 * would play at once.
 */
export const SingleVideoCoordinatorProvider = ({ children }: { children: ReactNode }) => {
    const entries = useRef(new Map<string, Entry>());
    const [soundEnabled, setSoundEnabled] = useState(false);

    const recompute = useCallback(() => {
        let bestId: string | null = null;
        let bestRatio = 0;
        entries.current.forEach((entry, id) => {
            if (entry.isPlaybackCandidate && entry.ratio > bestRatio) {
                bestRatio = entry.ratio;
                bestId = id;
            }
        });
        // Only notify videos whose play state actually flips, to avoid churn while scrolling.
        entries.current.forEach((entry, id) => {
            const shouldPlay = id === bestId;
            if (entry.playing !== shouldPlay) {
                entry.playing = shouldPlay;
                entry.setPlaying(shouldPlay);
            }
        });
    }, []);

    // Debounced so a mid-swipe frame — where the swiped tile's ratio dips below a stationary
    // neighbour's — can't briefly steal playback. `scroll` fires during the snap too, so it's covered.
    const settleRecompute = useDebounce(recompute, SETTLE_MS);

    // Capture: scroll doesn't bubble up from the nested carousel scrollers.
    useEffect(() => {
        document.addEventListener('scroll', settleRecompute, true);
        return () => document.removeEventListener('scroll', settleRecompute, true);
    }, [settleRecompute]);

    const register = useCallback(
        (id: string, setPlaying: (playing: boolean) => void) => {
            entries.current.set(id, { ratio: 0, isPlaybackCandidate: false, playing: false, setPlaying });
            return () => {
                entries.current.delete(id);
                settleRecompute();
            };
        },
        [settleRecompute]
    );

    const report = useCallback(
        (id: string, ratio: number, isPlaybackCandidate: boolean) => {
            const entry = entries.current.get(id);
            if (!entry) {
                return;
            }
            entry.ratio = ratio;
            entry.isPlaybackCandidate = isPlaybackCandidate;
            settleRecompute();
        },
        [settleRecompute]
    );

    const toggleSound = useCallback(() => setSoundEnabled((enabled) => !enabled), []);

    const value = useMemo<SingleVideoCoordinator>(
        () => ({ soundEnabled, toggleSound, register, report }),
        [soundEnabled, toggleSound, register, report]
    );

    return <CoordinatorContext.Provider value={value}>{children}</CoordinatorContext.Provider>;
};

export type CoordinatedVideoState = {
    isPlaying: boolean;
    soundEnabled: boolean;
    toggleSound: () => void;
};

/**
 * How a `Video` plugs into the page coordinator: it registers under `elementId`, reports its
 * visibility, and gets back whether it's the one allowed to play plus the shared sound state.
 * Returns null when no provider is mounted, so the `Video` falls back to standalone in-view autoplay.
 */
export const usePageVideoCoordinator = (
    elementId: string,
    isPlaybackCandidate: boolean
): CoordinatedVideoState | null => {
    const coordinator = useContext(CoordinatorContext);
    const [isPlaying, setIsPlaying] = useState(false);
    const isCandidateRef = useRef(isPlaybackCandidate);
    const ratioRef = useRef(0);

    // Depend on the stable `register`/`report`, not the whole coordinator object — it is rebuilt on
    // every `soundEnabled` change, and re-registering on each mute toggle desyncs `playing` (>1 plays).
    const register = coordinator?.register;
    const report = coordinator?.report;

    useEffect(() => {
        if (!register || !report) {
            return undefined;
        }
        const unregister = register(elementId, setIsPlaying);
        const element = document.getElementById(elementId);
        if (!element) {
            return unregister;
        }
        // Reports visibility for candidate selection; `Video` keeps its own 0.99 observer for play gating.
        const observer = new IntersectionObserver(
            ([entry]) => {
                ratioRef.current = entry.isIntersecting ? entry.intersectionRatio : 0;
                report(elementId, ratioRef.current, isCandidateRef.current);
            },
            { threshold: RATIO_THRESHOLDS }
        );
        observer.observe(element);
        return () => {
            observer.disconnect();
            unregister();
            // Match the fresh (playing:false) entry a re-registration creates, so the flip-only
            // notification can't leave this video stuck playing (no-op on a real unmount).
            setIsPlaying(false);
        };
    }, [register, report, elementId]);

    useEffect(() => {
        isCandidateRef.current = isPlaybackCandidate;
        report?.(elementId, ratioRef.current, isPlaybackCandidate);
    }, [report, elementId, isPlaybackCandidate]);

    if (!coordinator) {
        return null;
    }
    return { isPlaying, soundEnabled: coordinator.soundEnabled, toggleSound: coordinator.toggleSound };
};
